import { t, getCurrentLanguage } from '@i18n';
import { InstallPWAButton } from './InstallPWAButton.js';
import './InstallPWAButton.css';

// Hours config — single source of truth
const HOURS = [
  { day: 'monday',    open: null,    close: null },
  { day: 'tuesday',   open: '09:00', close: '21:00' },
  { day: 'wednesday', open: '09:00', close: '21:00' },
  { day: 'thursday',  open: '09:00', close: '21:00' },
  { day: 'friday',    open: '09:00', close: '21:00' },
  { day: 'saturday',  open: '09:00', close: '21:00' },
  { day: 'sunday',    open: '09:00', close: '21:00' },
];

const DAY_LABELS = {
  en: {
    monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday',
    thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday',
  },
  es: {
    monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miércoles',
    thursday: 'Jueves', friday: 'Viernes', saturday: 'Sábado', sunday: 'Domingo',
  },
};

function formatHours(open, close, lang) {
  if (!open || !close) return lang === 'es' ? 'Cerrado' : 'Closed';
  const fmt = (hhmm) => {
    const [h] = hhmm.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const hr = h % 12 || 12;
    return `${hr} ${period}`;
  };
  return `${fmt(open)} – ${fmt(close)}`;
}

function isOpenNow() {
  const now = new Date();
  const day = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'][now.getDay()];
  const today = HOURS.find(h => h.day === day);
  if (!today || !today.open) return false;
  const [oh, om] = today.open.split(':').map(Number);
  const [ch, cm] = today.close.split(':').map(Number);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  return nowMin >= oh * 60 + om && nowMin < ch * 60 + cm;
}

export function Footer() {
  const lang = getCurrentLanguage();
  const todayKey = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'][new Date().getDay()];
  const open = isOpenNow();

  const T = {
    nav: lang === 'es' ? 'Navegación' : 'Navigation',
    hours: lang === 'es' ? 'Horario' : 'Hours',
    contact: lang === 'es' ? 'Contacto' : 'Contact',
    home: lang === 'es' ? 'Inicio' : 'Home',
    services: lang === 'es' ? 'Servicios' : 'Services',
    gallery: lang === 'es' ? 'Galería' : 'Gallery',
    about: lang === 'es' ? 'Nosotros' : 'About',
    contactNav: lang === 'es' ? 'Contacto' : 'Contact',
    bookBtn: lang === 'es' ? 'Reservar Cita' : 'Book Appointment',
    tagline: lang === 'es' ? 'Cortes de precisión. Experiencia premium.' : 'Precision cuts. Premium experience.',
    rights: lang === 'es' ? 'Todos los derechos reservados.' : 'All rights reserved.',
    privacy: lang === 'es' ? 'Política de Privacidad' : 'Privacy Policy',
    terms: lang === 'es' ? 'Términos de Servicio' : 'Terms of Service',
    staffLogin: lang === 'es' ? 'Acceso Personal' : 'Staff Login',
    openNow: lang === 'es' ? 'Abierto ahora' : 'Open now',
    closedNow: lang === 'es' ? 'Cerrado' : 'Closed',
    address: lang === 'es' ? 'Dirección' : 'Address',
    phone: lang === 'es' ? 'Teléfono' : 'Phone',
  };

  const hoursRows = HOURS.map(h => {
    const isToday = h.day === todayKey;
    const isClosed = !h.open;
    return `
      <div class="footer-hours-row${isClosed ? ' closed' : ''}${isToday ? ' today' : ''}">
        <span>${DAY_LABELS[lang][h.day]}</span>
        <span>${formatHours(h.open, h.close, lang)}</span>
      </div>
    `;
  }).join('');

  const footer = document.createElement('footer');
  footer.className = 'footer';

  footer.innerHTML = `
    <!-- ================================================================
         DESKTOP
         ================================================================ -->
    <div class="footer-desktop">
      <div class="footer-inner">

        <div class="footer-brand">
          <div class="footer-logo">
            <img src="/assets/images/logo.png" alt="Elite Fade Designs" class="footer-logo-img" />
            <span class="footer-brand-name">Elite Fade Designs</span>
          </div>
          <p class="footer-tagline">${T.tagline}</p>
          <div class="footer-social">
            <a href="https://instagram.com" target="_blank" rel="noopener" aria-label="Instagram" class="footer-social-link">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                <circle cx="12" cy="12" r="4"/>
                <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none"/>
              </svg>
            </a>
            <a href="https://facebook.com" target="_blank" rel="noopener" aria-label="Facebook" class="footer-social-link">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
              </svg>
            </a>
          </div>
        </div>

        <div class="footer-col">
          <h4 class="footer-col-title">${T.nav}</h4>
          <nav class="footer-nav">
            <a href="/" data-link class="footer-link">${T.home}</a>
            <a href="/services" data-link class="footer-link">${T.services}</a>
            <a href="/gallery" data-link class="footer-link">${T.gallery}</a>
            <a href="/about" data-link class="footer-link">${T.about}</a>
            <a href="/contact" data-link class="footer-link">${T.contactNav}</a>
          </nav>
        </div>

        <div class="footer-col">
          <h4 class="footer-col-title">${T.hours}</h4>
          <div class="footer-hours">${hoursRows}</div>
        </div>

        <div class="footer-col">
          <h4 class="footer-col-title">${T.contact}</h4>
          <div class="footer-contact-list">
            <a href="https://maps.google.com/?q=1702+Washington+St+Waukegan+IL+60085"
               target="_blank" rel="noopener" class="footer-contact-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
              <span>1702 Washington St<br/>Waukegan, IL 60085</span>
            </a>
            <a href="tel:+12246007753" class="footer-contact-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.19h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.77a16 16 0 0 0 6 6l.87-.87a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.46 16.92z"/>
              </svg>
              <span>(224) 600-7753</span>
            </a>
          </div>
          <a href="/booking" data-link class="footer-book-btn">${T.bookBtn}</a>
        </div>

      </div>

      <div class="footer-bottom">
        <span>© ${new Date().getFullYear()} Elite Fade Designs. ${T.rights}</span>
        <div class="footer-bottom-links" id="footer-bottom-links-desktop">
          <a href="/policies" data-link>${T.privacy}</a>
          <a href="/policies" data-link>${T.terms}</a>
          <a href="/efd-admin" data-link class="footer-staff-link">${T.staffLogin}</a>
        </div>
      </div>
    </div>

    <!-- ================================================================
         MOBILE
         ================================================================ -->
    <div class="footer-mobile">
      <div class="footer-m-brand">
        <img src="/assets/images/logo.png" alt="Elite Fade" class="footer-m-logo" />
        <div class="footer-m-brand-text">
          <h4>Elite Fade Designs</h4>
          <p>${T.tagline}</p>
        </div>
      </div>

      <div class="footer-m-status ${open ? 'is-open' : ''}">
        <span class="footer-m-status-dot"></span>
        <span class="footer-m-status-label">${open ? T.openNow : T.closedNow}</span>
        ${(() => {
          const today = HOURS.find(h => h.day === todayKey);
          if (!today || !today.open) return '';
          return `
            <span class="footer-m-status-sep">·</span>
            <span class="footer-m-status-detail">${formatHours(today.open, today.close, lang)}</span>
          `;
        })()}
      </div>

      <div class="footer-m-actions">
        <a href="https://maps.google.com/?q=1702+Washington+St+Waukegan+IL+60085"
           target="_blank" rel="noopener" class="footer-m-action">
          <span class="footer-m-action-icon">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
          </span>
          <div class="footer-m-action-content">
            <p class="footer-m-action-title">${T.address}</p>
            <p class="footer-m-action-sub">1702 Washington St, Waukegan, IL 60085</p>
          </div>
          <svg class="footer-m-action-chevron" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </a>

        <a href="tel:+12246007753" class="footer-m-action">
          <span class="footer-m-action-icon">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.19h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.77a16 16 0 0 0 6 6l.87-.87a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21.46 16.92z"/>
            </svg>
          </span>
          <div class="footer-m-action-content">
            <p class="footer-m-action-title">${T.phone}</p>
            <p class="footer-m-action-sub">(224) 600-7753</p>
          </div>
          <svg class="footer-m-action-chevron" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </a>
      </div>

      <div class="footer-m-social">
        <a href="https://instagram.com" target="_blank" rel="noopener" aria-label="Instagram" class="footer-m-social-link">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
            <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
            <circle cx="12" cy="12" r="4"/>
            <circle cx="17.5" cy="6.5" r="0.5" fill="currentColor" stroke="none"/>
          </svg>
        </a>
        <a href="https://facebook.com" target="_blank" rel="noopener" aria-label="Facebook" class="footer-m-social-link">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
          </svg>
        </a>
      </div>

      <div class="footer-m-bottom">
        <p>© ${new Date().getFullYear()} Elite Fade Designs</p>
        <div class="footer-m-legal" id="footer-bottom-links-mobile">
          <a href="/policies" data-link>${T.privacy}</a>
          <span>·</span>
          <a href="/efd-admin" data-link>${T.staffLogin}</a>
        </div>
      </div>
    </div>
  `;

  // Inject install button into desktop bottom links (only desktop has it)
  const bottomLinks = footer.querySelector('#footer-bottom-links-desktop');
  if (bottomLinks) {
    bottomLinks.appendChild(InstallPWAButton());
  }

  // SPA navigation
  footer.querySelectorAll('[data-link]').forEach(link => {
    link.addEventListener('click', e => {
      e.preventDefault();
      const path = link.getAttribute('href');
      window.history.pushState({}, '', path);
      window.dispatchEvent(new PopStateEvent('popstate'));
    });
  });

  return footer;
}
